import {useCallback, useEffect, useMemo, useState} from 'react';
import queryString from 'query-string';
import url from 'url';
import dayjs from 'dayjs';
import {useHistory, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import {
  getAllProcessTypeDetails,
  getAvailableProcessAssigneeGroups,
  getAvailableProcesses,
  getRunningProcesses,
  startSubProcess
} from '../../../api/processes';
import {setTitle} from '../../../store/header/header.slice';
import {useAppDispatch, useAppSelector} from '../../../store/hooks';
import Error from '../../common/Error';
import {Subprocess, getSubprocessToastMessage} from '../../../constants/constants';
import {getSortedParams} from '../../../helpers';
import {GroupListProps, RunningProcessesProps} from '../../../constants/types';
import ConfirmationPopup from '../../common/popup/ConfirmationPopup';
import routes from '../../tasks/routes';
import {getAlertSettingsByProcessType} from '../../../api/settings/alert_alarm';
import {AlertSettingsProps} from '../../settings/alert_alarm/types';
import {ProcessFilter, SubprocessPopupData, ProcessActionPopupData} from '../types';
import RunningProcessesList from './RunningProcessesList';
import ProcessAssigneePopup from '../process_assignee/ProcessAssigneePopup';
import {DEFAULT_ERROR} from '../../../api/constants';
import {currentUserProps} from '../../../store/currentUser/currentUser.slice';
import {RootState} from '../../../store/store';
import {shouldShowProcessClaimingButton} from '../helpers';
import {useSnackbarHook} from '../../../hooks/useSnackbar';
import Filters from './Filters';

const initialQuery = {
  direction: 'desc',
  sort: 'patient',
  type: 'SCHEDULING',
  quick_filter: 'ALL'
};

type LocationParams = {
  state?: {
    filter?: ProcessFilter
  }
}

export const RunningProcesses = () => {
  const {t} = useTranslation('processes');
  const dispatch = useAppDispatch();
  const history = useHistory();
  const {state}: LocationParams = useLocation();
  const {filter: initialFilter} = state || {};
  const [isLoading, setLoading] = useState(false);
  const [processesData, setProcessesData] =
  useState<RunningProcessesProps>([]);
  const [subProcessesData, setSubProcessesData] = useState([]);
  const [processAlertData, setProcessAlertData] =
    useState<Nullable<AlertSettingsProps>>(null);
  const [filterState, setFilterState] =
    useState<Nullable<ProcessFilter>>(initialFilter || null);
  const [error, setError] = useState('');
  const [typeOptions, setTypeOptions] = useState([]);
  const [subprocessPopupData, setSubprocessPopupData] =
    useState<Nullable<SubprocessPopupData>>(null);
  const [isActionProcessing, setIsActionProcessing] = useState(false);
  const [processActionsPopupData, setProcessActionsPopupData] =
    useState<Nullable<ProcessActionPopupData>>(null);
  const [availableProcessAssigneeGroups, setAvailableProcessAssigneeGroups] =
    useState<Nullable<GroupListProps[]>>(null);
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  const currentUser: currentUserProps = useAppSelector(
      (state: RootState) => state.user
  );

  const {id: currentUserId, groups: userGroups = []} = currentUser || {};

  const hasProcessClaimButton = useMemo(() =>
    shouldShowProcessClaimingButton(
        {availableProcessAssigneeGroups, userGroups}
    ), [availableProcessAssigneeGroups, userGroups]);

  const {pathname, search} = useLocation();
  const query = {
    ...initialQuery,
    ...getSortedParams(queryString.parse(search))
  };

  const {
    type: processType,
    sort: sortedBy,
    direction: sortDirection,
    quick_filter: quickFilter
  } = query;

  useEffect(() => {
    history.replace(
        url.format({
          pathname,
          query
        }));
  }, []);

  useEffect(() => {
    getAllProcessTypes();
    dispatch(setTitle(t('pageTitle.processInstances', {processType: t(`processNames.${processType}`)})));
  }, [processType]);

  useEffect(() => {
    getAllProcesses();
  }, [processType, quickFilter]);

  useEffect(() => {
    if (!sortedBy || !sortDirection) return;

    if (sortedBy === 'patient') {
      setProcessesData(processesData.sort((a, b) =>
      sortDirection === 'asc' ?
      `${a[sortedBy].firstName} ${a[sortedBy].lastName}`
          .localeCompare(`${b[sortedBy].firstName} ${b[sortedBy].lastName}`) :
          `${b[sortedBy].firstName} ${b[sortedBy].lastName}`.
              localeCompare(`${a[sortedBy].firstName} ${a[sortedBy].lastName}`)
      ));
    }

    if (sortedBy === 'status') {
      setProcessesData(processesData.sort((a, b) => {
        if (sortDirection === 'asc') {
          return getExpiredTime(a) - getExpiredTime(b);
        } else {
          return getExpiredTime(b) - getExpiredTime(a);
        }
      }
      ));
    }
  }, [sortedBy, sortDirection, processesData]);

  const getAllProcessTypes = async () => {
    try {
      const processTypeDetailsResponse = await getAllProcessTypeDetails();
      const formedTypeOptions =
        processTypeDetailsResponse.map(({code, description}) => ({
          value: code,
          label: description
        }));

      setTypeOptions(formedTypeOptions);
    } catch (error) {
      setError(error?.message || '');
    }
  };

  const getAllProcesses = useCallback(async () => {
    setLoading(true);

    try {
      const [
        runningProcessesResponse,
        subProcessesResponse,
        processAlertResponse,
        availableProcessAssigneeGroupsResponse
      ] = await Promise.all([
        getRunningProcesses(processType, quickFilter),
        getAvailableProcesses(processType),
        getAlertSettingsByProcessType(processType),
        getAvailableProcessAssigneeGroups(processType)
      ]);
      const subProcessesCodes = subProcessesResponse.map((elem) => elem.code);

      setProcessesData(runningProcessesResponse);
      setSubProcessesData(subProcessesCodes);
      setProcessAlertData(processAlertResponse);
      setAvailableProcessAssigneeGroups(availableProcessAssigneeGroupsResponse);
    } catch (error) {
      setError(error?.message || DEFAULT_ERROR);
    } finally {
      setLoading(false);
    }
  }, [processType, quickFilter]);

  const getAheadProcesses = useCallback(async () => {
    setFilterState((prevState) => prevState === ProcessFilter.AHEAD ? null:
    ProcessFilter.AHEAD);
  }, []);

  const getBehindProcesses = useCallback(async () => {
    setFilterState((prevState) => prevState === ProcessFilter.BEHIND ? null:
    ProcessFilter.BEHIND);
  }, []);

  const getExpiredTime = (data) => {
    const currentTime = dayjs();
    const formattedStartAt = dayjs.unix(data.startedAt.seconds);
    const formattedTargetTime = dayjs(formattedStartAt)
        .add(data.targetTime, 's');

    const duration = dayjs.duration(dayjs(currentTime)
        .diff(dayjs(formattedTargetTime)));

    return Math.floor(duration.asSeconds());
  };

  const makeSubProcessAction = useCallback(async (
      id: number,
      subProcessType: Subprocess,
      patientName: string
  ) => {
    setIsActionProcessing(true);
    const requestBody = {
      'processId': id,
      'subProcessType': subProcessType
    };

    try {
      await startSubProcess(id, requestBody);
      const toastMessage =
          getSubprocessToastMessage(subProcessType, processType, patientName);
      addSuccessToast(toastMessage);
      if (subProcessType === Subprocess.TERMINATE) {
        setProcessesData((prevState) => {
          return prevState.filter(({id: processId}) => processId !== id);
        });
      }
    } catch (error) {
      addErrorToast(error.message);
    } finally {
      setIsActionProcessing(false);
      setSubprocessPopupData(null);

      if (subProcessType === Subprocess.EARLY_ARRIVAL ||
          subProcessType === Subprocess.RESCHEDULE_APPOINTMENT) {
        history.push(routes.tasks());
      }
    }
  }, [processType]);

  const getRunningProcessesResponse = useCallback(async () => {
    try {
      const runningProcessesResponse =
      await getRunningProcesses(processType, quickFilter);
      setProcessesData(runningProcessesResponse);
    } catch (error) {
      setError(error?.message || DEFAULT_ERROR);
    }
  }, [quickFilter, processType]);

  return (
    <>
      <div className="container">
        <div className="running-processes_sorting-wrapper">
          <div className="running-processes_ahead-wrapper">
            <div
              className={classNames('running-processes_ahead', {'active': filterState === ProcessFilter.AHEAD, 'disabled': isLoading})}
              onClick={getAheadProcesses}
            >
              {t('processAheadFilter')}
            </div>
            <div
              className={classNames('running-processes_ahead', {'active': filterState === ProcessFilter.BEHIND, 'disabled': isLoading})}
              onClick={getBehindProcesses}
            >
              {t('processBehindFilter')}
            </div>
          </div>
          <Filters
            query={query}
            pathname={pathname}
            isLoading={isLoading}
            typeOptions={typeOptions}
          />
        </div>
        <RunningProcessesList
          processesData={processesData}
          isLoading={isLoading}
          subProcessesData={subProcessesData}
          processAlertData={processAlertData}
          error={error}
          setError={setError}
          setSubprocessPopupData={setSubprocessPopupData}
          setProcessActionsPopupData={setProcessActionsPopupData}
          filterState={filterState}
          currentUserId={currentUserId}
          hasProcessClaimButton={hasProcessClaimButton}
        />

        {error && <Error message={error} />}
      </div>

      {subprocessPopupData && (
        <ConfirmationPopup
          labels={{body: subprocessPopupData.message || ''}}
          isOpen={!!subprocessPopupData}
          setIsOpen={() => setSubprocessPopupData(null)}
          confirmableAction={() => makeSubProcessAction(
              subprocessPopupData.id,
              subprocessPopupData.type,
              subprocessPopupData.patientName
          )}
          isActionProcessing={isActionProcessing}
        />
      )}

      <ProcessAssigneePopup
        processActionsPopupData={processActionsPopupData}
        setProcessActionsPopupData={setProcessActionsPopupData}
        isActionProcessing={isActionProcessing}
        setIsActionProcessing={setIsActionProcessing}
        getProcessData={getRunningProcessesResponse}
      />
    </>
  );
};

export default RunningProcesses;
